import { z } from "zod";

/**
 * Student Schema - ข้อมูลนักเรียน
 */
/** ค่าเพศที่ใช้ในระบบ: male (ชาย), female (หญิง), other (อื่นๆ) */
export const GENDER_VALUES = ["male", "female", "other"] as const;
export type Gender = (typeof GENDER_VALUES)[number];

const legalNameField = (message: string) =>
  z
    .string()
    .trim()
    .min(1, message)
    .refine((value) => !value.startsWith("("), {
      message:
        "ຊື່ຈິງບໍ່ຕ້ອງຂຶ້ນຕົ້ນດ້ວຍວົງເລັບ — ໃສ່ຊື່ຫຼີ້ນໃນຊ່ອງຊື່ຫຼີ້ນ",
    });

const nicknameField = z.string().trim().max(120).nullable().optional();

export const StudentSchema = z.object({
  id: z.string(),
  memberId: z.string(),
  name: z.string(),
  nickname: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  email: z.string().nullable(),
  /** วันเดือนปีเกิด (YYYY-MM-DD) */
  dateOfBirth: z.string().nullable(),
  /** เพศ: male, female, other */
  gender: z.enum(GENDER_VALUES).nullable(),
  status: z.enum(["active", "deactivated"]),
  notes: z.string().nullable(),
  /** รูปภาพโปรไฟล์ - key ใน MinIO */
  image: z.string().nullable(),
  /** เอกสารสมาชิกที่ลงนามแล้ว — S3 key */
  membershipSignedDocumentFileId: z.string().nullable(),
  membershipSignedDocumentUploadedAt: z.string().nullable(),
  groupId: z.string().nullable(),
  groupName: z.string().nullable(),
  whatsappOptIn: z.boolean(),
  whatsappOptInAt: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type StudentDTO = z.infer<typeof StudentSchema>;

/**
 * Create Student Schema
 * Member ID จะถูก auto-generate ที่ server
 */
export const CreateStudentSchema = z.object({
  name: legalNameField("ຊື່ຕ້ອງບໍ່ວ່າງ"),
  nickname: nicknameField,
  phoneNumber: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  dateOfBirth: z.string().nullable().optional(),
  gender: z.enum(GENDER_VALUES).nullable().optional(),
  status: z.enum(["active", "deactivated"]).default("active"),
  notes: z.string().nullable().optional(),
  /** Key ใน MinIO หรือ null ถ้าลบรูป */
  image: z.string().nullable().optional(),
  groupId: z.string().nullable().optional(),
  whatsappOptIn: z.boolean().default(false),
});
export type CreateStudentDTO = z.infer<typeof CreateStudentSchema>;

/**
 * Update Student Schema
 * Member ID ห้ามแก้ไข (readonly)
 */
export const UpdateStudentSchema = z.object({
  name: legalNameField("ຊື່ຕ້ອງບໍ່ວ່າງ").optional(),
  nickname: nicknameField,
  phoneNumber: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  dateOfBirth: z.string().nullable().optional(),
  gender: z.enum(GENDER_VALUES).nullable().optional(),
  status: z.enum(["active", "deactivated"]).optional(),
  notes: z.string().nullable().optional(),
  /** Key ใน MinIO หรือ null ถ้าลบรูป */
  image: z.string().nullable().optional(),
  groupId: z.string().nullable().optional(),
  whatsappOptIn: z.boolean().optional(),
});
export type UpdateStudentDTO = z.infer<typeof UpdateStudentSchema>;
